import {
  convertToDashedTripType,
  getInfantAndChildCount,
  translate,
} from '@web-platform/shared-universal-translations';
import { normalizeSearchLegs } from '@web-platform/shared-universal-utils-frontend/src/helpers/normalizeSearchLegs';
import {
  formatDateCustom,
  isMonthDate,
} from '@web-platform/shared-universal-utils-frontend-localisation';
import { getMonth } from 'date-fns/getMonth';
import { parseISO } from 'date-fns/parseISO';
import { createSelector } from 'reselect';

import { getUserSearch } from '../state';

import type { DateString } from '@web-platform/shared-universal-types/src/dateString';
import type { SearchLegPlace } from '@web-platform/shared-universal-types/src/redux/state/appInitial/user/search';
import type { Maybe } from '@web-platform/shared-universal-types-utils/src/utils';

export type { NormalizedSearchLeg } from '@web-platform/shared-universal-utils-frontend/src/helpers/normalizeSearchLegs';

export const getSearchLegs = createSelector([getUserSearch], ({ legs }) =>
  normalizeSearchLegs(legs),
);

const DAY_FORMAT_STRING = 'd';
const DAY_MONTH_FORMAT_STRING = 'MMMd';
const MONTH_FORMAT_STRING = 'MMM';

export const getOutboundDate = createSelector(
  [getSearchLegs],
  (legs): DateString => legs[0]?.date ?? '',
);

export const getInboundDate = createSelector(
  [getSearchLegs],
  (legs): DateString => (legs.length > 1 ? legs[legs.length - 1]?.date : '') ?? '',
);

export const getOrigin = createSelector(
  [getSearchLegs],
  (legs): SearchLegPlace | undefined => legs[0]?.origin || undefined,
);

export const getNearestOrigin = createSelector(
  [getUserSearch],
  ({ nearestOrigin }): Maybe<SearchLegPlace> => nearestOrigin,
);

export const getDestination = createSelector(
  [getSearchLegs],
  (legs): SearchLegPlace | undefined => legs[0]?.destination || undefined,
);

export const getPreferDirects = createSelector(
  [getUserSearch],
  ({ prefer_directs: preferDirects }) => preferDirects,
);

export const getFormattedSearchDates = createSelector(
  [getOutboundDate, getInboundDate],
  (outboundDate, inboundDate) => {
    if (!outboundDate) {
      return '';
    }

    if (isMonthDate(outboundDate)) {
      return inboundDate
        ? `${formatDateCustom(outboundDate, MONTH_FORMAT_STRING)} - ${formatDateCustom(
            inboundDate,
            MONTH_FORMAT_STRING,
          )}`
        : formatDateCustom(outboundDate, MONTH_FORMAT_STRING);
    }

    if (!inboundDate) {
      return formatDateCustom(outboundDate, DAY_MONTH_FORMAT_STRING);
    }

    if (getMonth(parseISO(outboundDate)) === getMonth(parseISO(inboundDate))) {
      return translate('label_dates_same_month', {
        inboundDay: formatDateCustom(inboundDate, DAY_FORMAT_STRING),
        month: formatDateCustom(outboundDate, MONTH_FORMAT_STRING),
        outboundDay: formatDateCustom(outboundDate, DAY_FORMAT_STRING),
      });
    }

    return translate('label_dates_different_months', {
      inboundDay: formatDateCustom(inboundDate, DAY_FORMAT_STRING),
      inboundMonth: formatDateCustom(inboundDate, MONTH_FORMAT_STRING),
      outboundDay: formatDateCustom(outboundDate, DAY_FORMAT_STRING),
      outboundMonth: formatDateCustom(outboundDate, MONTH_FORMAT_STRING),
    });
  },
);

export const getFormattedOutboundDate = createSelector([getOutboundDate], (outboundDate) => {
  if (!outboundDate) {
    return '';
  }
  if (isMonthDate(outboundDate)) {
    return formatDateCustom(outboundDate, MONTH_FORMAT_STRING);
  }
  return formatDateCustom(outboundDate, DAY_MONTH_FORMAT_STRING);
});

export const getFormattedInboundDate = createSelector([getInboundDate], (inboundDate) => {
  if (!inboundDate) {
    return '';
  }
  if (isMonthDate(inboundDate)) {
    return formatDateCustom(inboundDate, MONTH_FORMAT_STRING);
  }
  return formatDateCustom(inboundDate, DAY_MONTH_FORMAT_STRING);
});

export const getAdults = createSelector([getUserSearch], ({ adults }) => adults);

export const getCabinClass = createSelector([getUserSearch], ({ cabinClass }) => cabinClass);

export const getTripType = createSelector([getUserSearch], ({ tripType }) => tripType);

export const getChildren = createSelector(
  [getUserSearch],
  ({ child_ages: children = [] }) => getInfantAndChildCount(children).childCount,
);

export const getInfants = createSelector(
  [getUserSearch],
  ({ child_ages: children = [] }) => getInfantAndChildCount(children).infantCount,
);

export const getTotalPassengers = createSelector(
  [getUserSearch],
  ({ adults, child_ages: children = [] }) => adults + children.length,
);

export const getDashedTripType = createSelector([getUserSearch], ({ tripType }) =>
  convertToDashedTripType(tripType),
);

export const isSelectedMonth = createSelector([getOutboundDate], (outboundDate) =>
  isMonthDate(outboundDate),
);
